import { type ApiErrorResponse, type ApiResponse, ORG_CONTEXT_HEADER } from '@cmoh/shared'
import type { AxiosRequestConfig } from 'axios'
import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { env } from '../config/env'
import { getTabOrganizationId, recoverFromRejectedTabOrganization } from './tab-organization'

const SKIP_TAB_ORGANIZATION = 'skipTabOrganization'

/**
 * Request config that omits the tab organization header. For user-scoped endpoints that validate
 * an organization taken from their body: their org-context errors are about that organization, not
 * the tab's, so they must not trigger the tab organization recovery.
 */
export const WITHOUT_TAB_ORGANIZATION: AxiosRequestConfig & { [SKIP_TAB_ORGANIZATION]: true } = {
  [SKIP_TAB_ORGANIZATION]: true,
}

function skipsTabOrganization(config: AxiosRequestConfig): boolean {
  return SKIP_TAB_ORGANIZATION in config && config[SKIP_TAB_ORGANIZATION] === true
}

/**
 * Configuration for the axios instance
 */
const axiosConfig: AxiosRequestConfig = {
  baseURL: env.apiBaseUrl,
  timeout: 120000, // 2 minutes - extended for AI operations like company enrichment
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
}

/**
 * Create axios instance with base configuration
 */
export const apiClient: AxiosInstance = axios.create(axiosConfig)

/**
 * Request interceptor to add authentication token
 */
apiClient.interceptors.request.use(
  (config) => {
    // Skip interceptor for auth requests (Better Auth handles these)
    if (config.url?.startsWith('/auth/')) {
      return config
    }

    const organizationId = getTabOrganizationId()
    if (organizationId && !skipsTabOrganization(config)) {
      config.headers.set(ORG_CONTEXT_HEADER, organizationId)
    }

    // Get token from localStorage or sessionStorage
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    config.withCredentials = true

    // Let axios set Content-Type automatically for FormData (includes multipart boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Response interceptor to handle common errors and transform responses
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    // Skip interceptor for auth requests (Better Auth handles these)
    if (response.config.url?.startsWith('/auth/')) {
      return response
    }

    // Transform the response to match our API response format
    return response
  },
  (error) => {
    // Skip interceptor for auth requests (Better Auth handles these)
    if (error.config?.url?.startsWith('/auth/')) {
      return Promise.reject(error)
    }

    // Handle network errors
    if (!error.response) {
      const networkError = new Error('Network error. Please check your connection.')
      return Promise.reject(networkError)
    }

    // Handle API error responses
    if (error.response.data && typeof error.response.data === 'object') {
      const apiError = error.response.data as ApiErrorResponse

      if (apiError.success === false) {
        const sentOrganizationId = error.config?.headers?.get(ORG_CONTEXT_HEADER)
        if (typeof sentOrganizationId === 'string') {
          recoverFromRejectedTabOrganization(sentOrganizationId, apiError.error.code)
        }

        const isServerError = error.response.status >= 500
        const errorMessage = isServerError
          ? 'Algo salió mal en el servidor. Por favor intentá de nuevo.'
          : apiError.error.message || 'An error occurred'
        const apiErrorInstance = new Error(errorMessage) as Error & {
          statusCode: number
          errorCode?: string
          details?: Record<string, unknown>
        }
        apiErrorInstance.statusCode = error.response.status
        apiErrorInstance.errorCode = apiError.error.code
        apiErrorInstance.details = apiError.error.details
        return Promise.reject(apiErrorInstance)
      }
    }

    // Handle HTTP status errors
    const status = error.response.status
    let message = 'An error occurred'

    switch (status) {
      case 401:
        message = 'Unauthorized. Please log in again.'
        localStorage.removeItem('auth_token')
        sessionStorage.removeItem('auth_token')
        break
      case 403:
        message = 'Forbidden. You do not have permission to access this resource.'
        break
      case 404:
        message = 'Resource not found.'
        break
      case 422:
        message = 'Validation error. Please check your input.'
        break
      case 500:
        message = 'Internal server error. Please try again later.'
        break
      default:
        message = `Error ${status}: ${error.response.statusText || 'Unknown error'}`
    }

    const httpError = new Error(message) as Error & { statusCode: number }
    httpError.statusCode = status
    return Promise.reject(httpError)
  },
)

/**
 * Generic API request function with proper typing
 */
export async function apiRequest<TData>(config: AxiosRequestConfig): Promise<ApiResponse<TData>> {
  const response = await apiClient.request<ApiResponse<TData>>(config)
  return response.data
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <TData>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<TData>({ ...config, method: 'GET', url }),

  post: <TData>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<TData>({ ...config, method: 'POST', url, data }),

  put: <TData>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<TData>({ ...config, method: 'PUT', url, data }),

  patch: <TData>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<TData>({ ...config, method: 'PATCH', url, data }),

  delete: <TData>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<TData>({ ...config, method: 'DELETE', url }),
}

/**
 * Extracts human-readable field errors from a validation error's details.
 * Returns a formatted string like "mainKeyword: Too big: expected..." or null if no details found.
 */
export function extractValidationDetails(
  error: Error & { details?: Record<string, unknown> },
): string | null {
  const issues = error.details?.issues as
    | { fieldErrors?: Record<string, string[]>; formErrors?: string[] }
    | undefined

  if (!issues) return null

  const parts: string[] = []

  if (issues.formErrors?.length) {
    parts.push(...issues.formErrors)
  }

  if (issues.fieldErrors) {
    for (const [field, messages] of Object.entries(issues.fieldErrors)) {
      if (Array.isArray(messages)) {
        parts.push(`${field}: ${messages.join(', ')}`)
      }
    }
  }

  return parts.length > 0 ? parts.join(' | ') : null
}

export type { AxiosRequestConfig }
